/**
 * replyAllMentions — who a REPLY addresses (Miles, 2026-09-20).
 *
 * His ask, verbatim intent: "when someone clicks reply to a particular
 * message then it should auto-begin the message with all individuals that
 * are mentioned in the message being replied to, starting with the
 * individual that wrote the message getting replied to — since the user is
 * replying to all in that message, making it easier for the user."
 *
 * Pure: given the message being answered (who wrote it, its words), the
 * roster, the viewer and the thread's summon gate, return the ORDERED handles
 * the composer seeds — the author first, then everyone the message addressed
 * in the order they appear. The composer chip reads the same rule for its
 * suggestion (the "group of individuals writing / mentioned in the last
 * mention" half of the ask — mentionAffordance.ts), so the two surfaces can
 * never disagree about who a message addressed.
 *
 * The rules, each pinned by tests/threadMentions/replyAllMentions.test.ts:
 *   • the AUTHOR leads — a reply answers them first;
 *   • then each `@handle` in the message, in order of appearance, resolved by
 *     the SAME fail-closed resolver the send path uses (an unknown or
 *     ambiguous handle names nobody, so nobody is addressed by mistake);
 *     `@team` is kept as the room's own handle when the message addressed
 *     the room and there is a room to address;
 *   • never the viewer themself, never anyone twice; when the thread's
 *     participants are known, never someone outside the thread — the
 *     original message could not reach them either, and adding people
 *     stays a human, consented act (T-G2);
 *   • Prest0n ONLY where the summon gate makes his mention load-bearing (a
 *     thread that requires @Prest0n to summon him), and then only when the
 *     target is his message — the 2026-09-15 reply rule, byte for byte — or
 *     the message addressed him; where every message reaches him anyway the
 *     mention is noise and is not seeded;
 *   • a handle the draft already carries is not seeded again, judged the way
 *     the send will judge it (by resolution, the exact token, the room's
 *     token, Prest0n by his own predicate) — a re-press adds nothing twice;
 *   • at most REPLY_ALL_MAX_MENTIONS.
 *
 * Every member handle is one `deriveMentionHandle` verified round-trips to
 * exactly that person, so what is seeded is what the summon resolves.
 * Inserting a handle never adds anyone to a thread: the consent path runs at
 * send time and is untouched. Pure + React-free.
 */
import { mentionsAssistant } from './summonGate';
import { ASSISTANT_MENTION } from './suggestionMention';
import { deriveMentionHandle } from './mentionAutocomplete';
import {
  extractPlainMentionQueries,
  isTeamMentionQuery,
  resolveMentionsToUids,
  TEAM_MENTION_HANDLE,
} from './mentionResolver';
import type { MentionRosterEntry } from './mentionResolver';

/** Sentinel senderId the shared store uses for assistant turns. */
export const REPLY_ALL_ASSISTANT_SENDER_ID = 'assistant';
/** The room's own sentinel — never a person (mentionAutocomplete's TEAM_MENTION_CANDIDATE.uid). */
export const REPLY_ALL_TEAM_ID = 'team';
/** The ceiling on seeded handles — a reply addresses people; it is not a roster dump. */
export const REPLY_ALL_MAX_MENTIONS = 6;

export interface ReplyAllTarget {
  /** Who wrote the message: a person's uid, or the assistant sentinel. */
  senderId?: string | null;
  /** True for a person's message (the shared-store shape); false for Prest0n's. */
  isUser?: boolean;
  /** The message's words. */
  text?: string | null;
}

export interface ReplyAllMention {
  /** The plain handle to insert after `@` (no `@`), e.g. `Prest0n`, `team`, `justinz`. */
  handle: string;
  /** Who it addresses: a roster uid, REPLY_ALL_ASSISTANT_SENDER_ID, or REPLY_ALL_TEAM_ID. */
  uid: string;
  kind: 'assistant' | 'team' | 'member';
}

export interface ReplyAllMentionsArgs {
  target: ReplyAllTarget | null | undefined;
  /** The account roster — handles are derived from it and verified against it. */
  roster: ReadonlyArray<MentionRosterEntry> | null | undefined;
  /** The viewer — never seeded. */
  selfId?: string | null;
  /** The thread's summon gate: whether an @Prest0n mention is load-bearing here. */
  gates: { gatesThisThread: boolean; requireMention: boolean | null | undefined };
  /** The current draft (plain text): handles it already carries are not seeded again. */
  draftText?: string | null;
  /** The thread's participants when known; absent or empty ⇒ unknown, every roster member qualifies. */
  participantIds?: ReadonlySet<string> | ReadonlyArray<string> | null;
  /** Defaults to REPLY_ALL_MAX_MENTIONS. */
  limit?: number;
}

export interface ReplyAllMentionsResult {
  mentions: ReplyAllMention[];
  /** The handles in order — what the composer inserts, each behind an `@`. */
  handles: string[];
}

type Addressee = { uid: string; kind: ReplyAllMention['kind'] };

function norm(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

function toParticipantSet(v: ReplyAllMentionsArgs['participantIds']): ReadonlySet<string> | null {
  if (!v) return null;
  const set: ReadonlySet<string> = v instanceof Set
    ? (v as ReadonlySet<string>)
    : new Set(Array.from(v as ReadonlyArray<string>, (id) => String(id ?? '').trim()).filter(Boolean));
  return set.size > 0 ? set : null;
}

/** Whether the target is one of Prest0n's messages (either shape the transcript carries). */
function authoredByAssistant(target: ReplyAllTarget, senderId: string): boolean {
  if (senderId === REPLY_ALL_ASSISTANT_SENDER_ID) return true;
  if (target.isUser === false) return true;
  return target.isUser === undefined && !senderId;
}

/**
 * Every addressee the target names, in order: its author, then each mention
 * as it appears. One token at a time so the ORDER of appearance survives; the
 * resolver's two-tier, fail-closed rule is applied to each exactly as the
 * send applies it to the whole message.
 */
function orderedAddressees(target: ReplyAllTarget, roster: ReadonlyArray<MentionRosterEntry>): Addressee[] {
  const out: Addressee[] = [];
  const senderId = (target.senderId ?? '').trim();
  if (authoredByAssistant(target, senderId)) out.push({ uid: REPLY_ALL_ASSISTANT_SENDER_ID, kind: 'assistant' });
  else if (senderId) out.push({ uid: senderId, kind: 'member' });

  const text = typeof target.text === 'string' ? target.text : '';
  for (const query of extractPlainMentionQueries(text)) {
    if (isTeamMentionQuery(query)) {
      out.push({ uid: REPLY_ALL_TEAM_ID, kind: 'team' });
      continue;
    }
    if (mentionsAssistant(`@${query}`)) {
      out.push({ uid: REPLY_ALL_ASSISTANT_SENDER_ID, kind: 'assistant' });
      continue;
    }
    const resolved = resolveMentionsToUids(`@${query}`, roster);
    if (resolved.length === 1) out.push({ uid: resolved[0], kind: 'member' });
  }
  return out;
}

export function replyAllMentions(args: ReplyAllMentionsArgs): ReplyAllMentionsResult {
  const target = args.target;
  if (!target) return { mentions: [], handles: [] };
  const roster = (args.roster ?? []).filter((m) => m && String(m.userId ?? '').trim());
  const self = (args.selfId ?? '').trim();
  const mentionLoadBearing = args.gates?.gatesThisThread === true && args.gates?.requireMention !== false;
  const participants = toParticipantSet(args.participantIds);
  const limit = Math.max(0, Math.floor(args.limit ?? REPLY_ALL_MAX_MENTIONS));
  const draft = typeof args.draftText === 'string' ? args.draftText : '';

  // What the draft already addresses, judged the way the send will judge it.
  const draftUids = new Set(resolveMentionsToUids(draft, roster));
  const draftQueries = new Set(extractPlainMentionQueries(draft).map(norm));
  const draftHasAssistant = mentionsAssistant(draft);
  const draftHasTeam = Array.from(draftQueries).some((q) => isTeamMentionQuery(q));

  const mentions: ReplyAllMention[] = [];
  const seen = new Set<string>();
  for (const a of orderedAddressees(target, roster)) {
    if (mentions.length >= limit) break;
    if (seen.has(a.uid)) continue;
    seen.add(a.uid);
    if (a.kind === 'assistant') {
      if (!mentionLoadBearing || draftHasAssistant) continue;
      mentions.push({ handle: ASSISTANT_MENTION.slice(1), uid: a.uid, kind: 'assistant' });
      continue;
    }
    if (a.kind === 'team') {
      // The room — only when there is a room (a thread of one has nobody to
      // address as a group; an unknown thread is given the benefit).
      if (draftHasTeam || (participants !== null && participants.size < 2)) continue;
      mentions.push({ handle: TEAM_MENTION_HANDLE, uid: a.uid, kind: 'team' });
      continue;
    }
    if (a.uid === self) continue;
    if (participants !== null && !participants.has(a.uid)) continue;
    const member = roster.find((m) => String(m.userId).trim() === a.uid);
    if (!member) continue;
    const handle = deriveMentionHandle(member, roster);
    if (!handle) continue;
    if (draftUids.has(a.uid) || draftQueries.has(norm(handle))) continue;
    mentions.push({ handle, uid: a.uid, kind: 'member' });
  }
  return { mentions, handles: mentions.map((m) => m.handle) };
}
